// 分類の先読み(プリフェッチ)キャッシュ。
// ユーザーが「AIで整理」を押す前に裏で classify + firstMove を済ませておき、
// 押下時はキャッシュから即表示する(体感ゼロ秒)。
//
// - module-level Map でアプリ稼働中だけ保持(永続化しない)
// - 同一キーの二重実行を inflight Map でガード
// - 裏処理なので UI ローディングは出さず、エラーは握りつぶす

import { classifyTasks, suggestFirstMove } from "./ai-pwa";
import type { ClassifyResult, FirstMove, Task } from "./tauri";

export interface ClassificationBundle {
  classify: ClassifyResult;
  firstMove: FirstMove;
}

// 未完了タスクから安定キーを作る。id と更新時刻で構成し、並びに依存しないよう sort。
export function classificationKey(tasks: Task[]): string {
  if (tasks.length === 0) return "__empty__";
  return tasks
    .map((t) => `${t.id}:${t.updatedAt}`)
    .sort()
    .join("|");
}

const cache = new Map<string, ClassificationBundle>();
const inflight = new Map<string, Promise<ClassificationBundle | null>>();

export function getCached(key: string): ClassificationBundle | undefined {
  return cache.get(key);
}

export function setCached(key: string, bundle: ClassificationBundle): void {
  cache.set(key, bundle);
}

// 裏で分類+一手を実行してキャッシュする。実行中の Promise を返す(押下側が待てるように)。
// 既にキャッシュ済みなら何もせず null を返す。
export function prefetchClassification(tasks: Task[]): Promise<ClassificationBundle | null> {
  const key = classificationKey(tasks);

  // (a) キャッシュ済み → 何もしない
  if (cache.has(key)) return Promise.resolve(null);

  // (b) 実行中 → 二重実行しない。既存の Promise を再利用
  const running = inflight.get(key);
  if (running) return running;

  // (c) 無ければ裏で実行
  const snapshot = tasks.slice();
  const job = (async (): Promise<ClassificationBundle | null> => {
    try {
      const classify = await classifyTasks(snapshot);
      let firstMove: FirstMove = { taskId: null, message: "" };
      try {
        firstMove = await suggestFirstMove(snapshot);
      } catch {
        // 一手の失敗は分類結果を壊さない
      }
      const bundle: ClassificationBundle = { classify, firstMove };
      cache.set(key, bundle);
      return bundle;
    } catch {
      // 裏処理なのでエラーは握りつぶす(UI に出さない)
      return null;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, job);
  return job;
}
